export const LECTURE_UPLOAD_ACCEPT_FILE_TYPES =
  '.csv,.pdf,.pptx,.ppt,.docx,.doc,.txt,.png,.jpg,.jpeg,text/csv,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/plain,image/png,image/jpeg';

export const LECTURE_UPLOAD_ALLOWED_TYPES = [
  'text/csv',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'text/plain',
  'image/png',
  'image/jpeg',
] as const;

export const MAX_FILES_PER_UPLOAD = 15;
export const MAX_TOTAL_SIZE_MB = 200;
export const MAX_INDIVIDUAL_FILE_SIZE_MB = 200;
export const AMBASSADOR_MAX_TOTAL_SIZE_MB = 500;
export const AMBASSADOR_MAX_INDIVIDUAL_FILE_SIZE_MB = 200;

export const DAILY_LIMIT_WARNING =
  'Daily usage reached. Upgrade for unlimited uploads.';

export type UploadValidationError = {
  message: string;
  variant?: 'error';
  autoHideDuration: number;
  /** Free-tier upgrade nudge — rendered as the persistent paywall toast
   *  (X to close + upgrade CTA) rather than a plain auto-dismiss toast. */
  isPaywall?: boolean;
};

export const normalizeUploadFiles = (
  files?: FileList | File[] | null
): File[] => {
  if (!files) return [];
  return Array.isArray(files) ? files : Array.from(files);
};

export const getEffectiveUploadLimits = (isAmbassador: boolean) => ({
  maxTotalSizeMB: isAmbassador
    ? AMBASSADOR_MAX_TOTAL_SIZE_MB
    : MAX_TOTAL_SIZE_MB,
  maxIndividualFileSizeMB: isAmbassador
    ? AMBASSADOR_MAX_INDIVIDUAL_FILE_SIZE_MB
    : MAX_INDIVIDUAL_FILE_SIZE_MB,
});

export const isSupportedLectureUploadType = (type: string): boolean =>
  LECTURE_UPLOAD_ALLOWED_TYPES.includes(
    type as (typeof LECTURE_UPLOAD_ALLOWED_TYPES)[number]
  );

type ValidateLectureUploadBatchParams = {
  files: File[];
  oneFileMode?: boolean;
  isFreePlan: boolean;
  isUnlimited: boolean;
  /** Uploads left in today's free-plan allowance; null = unknown or unlimited
   *  (the backend enforces the real limit per file either way). */
  uploadsRemaining: number | null;
  maxTotalSizeMB: number;
  maxIndividualFileSizeMB: number;
};

export const validateLectureUploadBatch = ({
  files,
  oneFileMode = false,
  isFreePlan,
  isUnlimited,
  uploadsRemaining,
  maxTotalSizeMB,
  maxIndividualFileSizeMB,
}: ValidateLectureUploadBatchParams): UploadValidationError | null => {
  const selectedFileCount = files.length;

  if (
    isFreePlan &&
    !isUnlimited &&
    uploadsRemaining !== null &&
    selectedFileCount > uploadsRemaining
  ) {
    return {
      message:
        uploadsRemaining <= 0
          ? DAILY_LIMIT_WARNING
          : `The free plan allows ${uploadsRemaining} more document upload${
              uploadsRemaining === 1 ? '' : 's'
            } today. Please select fewer files or upgrade for unlimited uploads.`,
      autoHideDuration: 5000,
      isPaywall: true,
    };
  }

  const maxFiles = oneFileMode ? 1 : MAX_FILES_PER_UPLOAD;
  if (selectedFileCount > maxFiles) {
    return {
      message: oneFileMode
        ? 'Please select only one file at a time.'
        : `Too many files selected. Please upload a maximum of ${MAX_FILES_PER_UPLOAD} files at once.`,
      variant: 'error',
      autoHideDuration: 5000,
    };
  }

  const totalSizeBytes = files.reduce((sum, file) => sum + file.size, 0);
  const totalSizeMB = totalSizeBytes / (1024 * 1024);
  if (totalSizeMB > maxTotalSizeMB) {
    return {
      message: `Total file size exceeds ${maxTotalSizeMB}MB limit. Current total: ${totalSizeMB.toFixed(1)}MB. Please select fewer or smaller files.`,
      variant: 'error',
      autoHideDuration: 6000,
    };
  }

  const invalidFiles = files.filter(
    (file) => !isSupportedLectureUploadType(file.type)
  );

  if (invalidFiles.length > 0) {
    return {
      message: `${invalidFiles.length} file(s) have unsupported types: ${invalidFiles.map((f) => f.name).join(', ')}. Please upload only supported document, image, or text files.`,
      variant: 'error',
      autoHideDuration: 6000,
    };
  }

  const maxSizeBytes = maxIndividualFileSizeMB * 1024 * 1024;
  const oversizedFiles = files.filter((file) => file.size > maxSizeBytes);
  if (oversizedFiles.length > 0) {
    const filesWithSizes = oversizedFiles.map(
      (file) => `${file.name} (${(file.size / (1024 * 1024)).toFixed(1)}MB)`
    );

    return {
      message: `${oversizedFiles.length} file(s) exceed the ${maxIndividualFileSizeMB}MB individual file limit: ${filesWithSizes.join(', ')}. Please select smaller files.`,
      variant: 'error',
      autoHideDuration: 6000,
    };
  }

  return null;
};

/**
 * The effective per-document page cap from a `GET /ai/uploads/today`
 * response, or undefined when the response did not carry a usable value (an
 * older backend). Undefined means "unknown": callers skip the pre-check and
 * the server decides, so a per-user limit above the global default is never
 * refused in the browser.
 */
export const resolveDocumentPageLimit = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : undefined;

export type LecturePagePrecheckResult =
  { allowed: true } | { allowed: false; message: string };

/**
 * Decides whether a PDF may be uploaded given its client-side page count. A
 * null count (unknown: unreadable, encrypted, slow) or an undefined limit (not
 * loaded, or not reported by the backend) is allowed, because the server
 * still checks the page count after upload.
 */
export const evaluateLecturePagePrecheck = ({
  fileName,
  pageCount,
  limit,
}: {
  fileName: string;
  pageCount: number | null;
  limit: number | undefined;
}): LecturePagePrecheckResult => {
  if (limit === undefined || pageCount === null || pageCount <= limit) {
    return { allowed: true };
  }

  return {
    allowed: false,
    message: `${fileName} has ${pageCount} pages, which exceeds the ${limit}-page limit. Please upload a smaller document.`,
  };
};

/**
 * Longest the lecture batch summary waits for one mapping start. A start still
 * pending then is reported as started: the server keeps running it, and
 * mapping completion has its own toast.
 */
export const MAPPING_START_SUMMARY_WAIT_MS = 30_000;

/**
 * Resolves with `promise`'s value, or with `fallback` when it has not settled
 * within `ms`. The original promise keeps running; only the wait is capped.
 */
export const settleWithin = <T>(
  promise: Promise<T>,
  ms: number,
  fallback: T
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<T>((resolve) => {
    timer = setTimeout(() => resolve(fallback), ms);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

/**
 * How one file of a multi-file upload ended:
 * - `started`: uploaded and its mapping start was accepted (or is still
 *   pending after the wait cap; the server keeps running it);
 * - `refused`: refused before upload (type, page count, size); its own toast
 *   already named the reason;
 * - `upload_failed`: the transfer to storage failed;
 * - `blocked` / `failed`: uploaded, but the mapping start was refused;
 * - `skipped`: uploaded, but not started because the first file hit the
 *   daily limit.
 */
export type LectureBatchFileStatus =
  'started' | 'refused' | 'upload_failed' | 'blocked' | 'failed' | 'skipped';

export type LectureBatchFileResult = {
  fileName: string;
  status: LectureBatchFileStatus;
};

export type LectureBatchSummaryToast = {
  message: string;
  variant?: 'warning' | 'error';
  autoHideDuration: number;
};

const MAX_LISTED_FILE_NAMES = 3;

const formatFileNames = (names: string[]): string => {
  if (names.length <= MAX_LISTED_FILE_NAMES) return names.join(', ');

  const listed = names.slice(0, MAX_LISTED_FILE_NAMES).join(', ');

  return `${listed} and ${names.length - MAX_LISTED_FILE_NAMES} more`;
};

const namesWithStatus = (
  results: LectureBatchFileResult[],
  statuses: LectureBatchFileStatus[]
): string[] =>
  results.filter((r) => statuses.includes(r.status)).map((r) => r.fileName);

/**
 * The one toast that sums up a lecture upload batch, or null when no toast
 * should show. Mapping completion has its own later toast; this only reports
 * whether each file was uploaded and its mapping started. Files refused
 * before upload are counted but not named: their own toast gave the reason.
 */
export const buildLectureBatchSummary = (
  results: LectureBatchFileResult[],
  {
    oneFileMode = false,
    isStoppedByLimit = false,
  }: { oneFileMode?: boolean; isStoppedByLimit?: boolean } = {}
): LectureBatchSummaryToast | null => {
  const total = results.length;
  if (total === 0) return null;

  const uploadFailedNames = namesWithStatus(results, ['upload_failed']);

  // The daily limit stopped the batch: the surface already opened the limit
  // modal, which is the outcome. Only real transfer failures are worth adding.
  if (isStoppedByLimit) {
    if (uploadFailedNames.length === 0) return null;

    return {
      message: `Upload failed: ${formatFileNames(uploadFailedNames)}.`,
      variant: 'error',
      autoHideDuration: 6000,
    };
  }

  const startedCount = results.filter((r) => r.status === 'started').length;

  if (startedCount === total) {
    return {
      message: oneFileMode
        ? 'Successfully uploaded file.'
        : `Successfully uploaded ${total} file(s).`,
      autoHideDuration: 3000,
    };
  }

  if (total === 1) {
    // A refusal or a refused mapping start has already shown its own reason.
    if (results[0].status !== 'upload_failed') return null;

    return {
      message: 'File failed to upload.',
      variant: 'error',
      autoHideDuration: 5000,
    };
  }

  const notStartedNames = namesWithStatus(results, [
    'blocked',
    'failed',
    'skipped',
  ]);

  const parts = [`${startedCount} of ${total} files uploaded and started.`];
  if (uploadFailedNames.length > 0) {
    parts.push(`Upload failed: ${formatFileNames(uploadFailedNames)}.`);
  }
  if (notStartedNames.length > 0) {
    parts.push(
      `Uploaded but not started: ${formatFileNames(notStartedNames)}.`
    );
  }

  return {
    message: parts.join(' '),
    variant: startedCount > 0 ? 'warning' : 'error',
    autoHideDuration: 6000,
  };
};
